<script setup lang="ts">
import { ref, onBeforeMount, computed } from "vue";
import SegmentDO = App.Data.Segments.SegmentDO;
import { useApiClient } from "@/api/client";
import ExportTypeSegment from "@/modules/integrations/components/ConfigureIntegrationExecution/ExportTypeSegment.vue";
import DeployteqConfiguration from "@/modules/integrations/components/ConfigureIntegrationExecution/DeployteqConfiguration.vue";
import IntegrationDO = App.Data.Integrations.IntegrationDO;
import DeployteqExecutionDetailsDTO = App.Data.Integrations.DeployteqExecutionDetailsDTO;
import { required } from "@/ui/validation/validationRules";
import { t } from "@/plugins/i18n";
import SegmentExecutionDetailsDTO = App.Data.Integrations.SegmentExecutionDetailsDTO;
import { toastApiResult } from "@/toaster";
import { useRouter } from "vue-router";
import RTHADpConfiguration from "@/modules/integrations/components/ConfigureIntegrationExecution/RTHADpConfiguration.vue";
import dayjs from "dayjs";

const router = useRouter();

type SegmentSource = {
	type: "segment";
	sourceData: SegmentDO;
	availability: "available_on_reservation_segments";
};

// add anotherSource = {..} in the future.

type SourceProps = SegmentSource; // | anotherSource

const props = defineProps<SourceProps>();

const ExportTypeComponentMap = {
	segment: ExportTypeSegment,
};

const IntegrationComponentMap = {
	deployteq: DeployteqConfiguration,
	rtha_dp: RTHADpConfiguration,
};

const availableIntegrations = ref<IntegrationDO[]>([]);
const selectedIntegration = ref<IntegrationDO | null>(null);

type ExecutionPropertiesBySource = {
	segment: SegmentExecutionDetailsDTO;
};
const executionProperties = ref<
	ExecutionPropertiesBySource[typeof props.type] | null
>(null);

type IntegrationConfigurationPropertiesBySource = {
	deployteq: DeployteqExecutionDetailsDTO;
};
const integrationConfiguration = ref<
	| IntegrationConfigurationPropertiesBySource[typeof selectedIntegration.value.name_clean]
	| null
>(null);

const getAvailableIntegrations = async () => {
	isLoading.value = true;
	const response = await useApiClient("integrations").get({
		page: 1,
		itemsPerPage: 1000,
		sortBy: [],
		"filter[available_in]": props.availability,
	});

	if (response.isOk()) {
		availableIntegrations.value = response.value.data;
	}
	isLoading.value = false;
};

const isWizardVisible = defineModel<boolean>();
const currentStep = ref(1);
const isLoading = ref<boolean>(true);

const isStepOneValid = ref<boolean>(false);
const isStepTwoValid = ref<boolean>(false);
const isStepThreeValid = ref<boolean>(false);

const stepValidators = {
	1: isStepOneValid,
	2: isStepTwoValid,
	3: isStepThreeValid,
};

const isNextStepAvailable = computed(() => {
	return stepValidators[currentStep.value]?.value ?? false;
});

const createExecution = async () => {
	if (executionProperties.value?.configuration) {
		executionProperties.value.configuration.exportDate =
			dayjs().format("YYYY-MM-DD");
	}
	const executionConfiguration = {
		[props.type]: executionProperties.value,
		[selectedIntegration.value.name_clean]: integrationConfiguration.value,
	};
	const response = await toastApiResult(
		useApiClient("integrations").createExecution(
			selectedIntegration.value.name_clean,
			props.type,
			executionConfiguration
		)
	);

	if (response.isOk()) {
		isWizardVisible.value = false;
		router.push({
			name: "integrations.manage.activity",
			params: {
				uuid: selectedIntegration.value.uuid,
			},
		});
	}
};

onBeforeMount(() => getAvailableIntegrations());
</script>

<template>
	<v-dialog v-model="isWizardVisible" persistent width="60vw">
		<v-stepper v-model="currentStep" flat>
			<template v-slot:default="{ prev, next }">
				<v-card elevation="0">
					<v-toolbar style="border-radius: 0 !important" color="white" flat>
						<v-toolbar-title
							><i class="fa-light fa-wand-sparkles fa-xl mr-3"></i>Integration
							Wizard
						</v-toolbar-title>
						<v-spacer></v-spacer>

						<v-btn icon @click="isWizardVisible = false">
							<v-icon>mdi-close</v-icon>
						</v-btn>
					</v-toolbar>
					<v-divider />

					<v-sheet color="grey-lighten-5">
						<v-stepper-header style="box-shadow: unset !important">
							<v-stepper-item
								:color="currentStep > 1 ? 'teal' : null"
								:complete="currentStep > 1"
								:value="1"
								>Choose Integration</v-stepper-item
							>
							<v-divider />
							<v-stepper-item
								:color="currentStep > 2 ? 'teal' : null"
								:complete="currentStep > 2"
								:value="2"
								>Configure Source Properties</v-stepper-item
							>
							<v-divider />
							<v-stepper-item :value="3"
								>Configure Execution Details
							</v-stepper-item>
						</v-stepper-header>
					</v-sheet>

					<v-divider />

					<v-stepper-window class="ma-0">
						<v-stepper-window-item :value="1">
							<v-form v-model="isStepOneValid">
								<div class="pa-5 pb-3 d-flex" v-if="!isLoading">
									<h6 class="text-h6 mt-3 label">Choose integration:</h6>
									<v-select
										v-if="availableIntegrations.length > 0"
										placeholder="Select one of the available integrations"
										:rules="[required(t)]"
										variant="filled"
										hide-details
										v-model="selectedIntegration"
										item-title="company_name"
										return-object
										style="text-transform: capitalize"
										:items="availableIntegrations"
									/>
									<v-alert
										border="start"
										prominent
										color="error"
										variant="tonal"
										v-else
									>
										There are no integrations available at this time.
									</v-alert>
								</div>
							</v-form>
						</v-stepper-window-item>
						<v-stepper-window-item :value="2">
							<div class="pa-5 pb-3">
								<component
									:is="ExportTypeComponentMap[props.type]"
									:source-data="sourceData"
									v-model="executionProperties"
									v-model:input-valid="isStepTwoValid"
								/>
							</div>
						</v-stepper-window-item>
						<v-stepper-window-item :value="3">
							<div class="pa-5 pb-3">
								<component
									:is="IntegrationComponentMap[selectedIntegration.name_clean]"
									v-model="integrationConfiguration"
									v-model:input-valid="isStepThreeValid"
								/>
							</div>
						</v-stepper-window-item>
					</v-stepper-window>

					<v-divider class="mt-2" />

					<v-card-actions class="pa-3">
						<v-btn variant="text" color="error" @click="isWizardVisible = false"
							>Cancel</v-btn
						>
						<v-spacer />
						<v-btn
							:disabled="currentStep === 1"
							class="ml-5"
							variant="text"
							color="teal"
							@click="prev"
							>Previous</v-btn
						>
						<v-btn
							:disabled="!isNextStepAvailable"
							class="ml-5"
							variant="tonal"
							color="teal"
							v-if="currentStep === 3"
							@click="createExecution"
							>Export through Integration</v-btn
						>
						<v-btn
							:disabled="!isNextStepAvailable"
							class="ml-5"
							variant="tonal"
							color="teal"
							v-else
							@click="next"
							>Next</v-btn
						>
					</v-card-actions>
				</v-card>
			</template>
		</v-stepper>
	</v-dialog>
</template>

<style lang="scss" scoped>
.label {
	width: 250px;
}
</style>
